import React, { InputHTMLAttributes, forwardRef } from 'react';
import { useExperience } from '../../context/ExperienceContext';
import { mv3 } from '../../styles/MV3_Styles';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'multiline'> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  multiline?: boolean;
  helpText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, leftIcon, rightIcon, onRightIconClick, multiline = false, helpText, className = '', ...props }, ref) => {
    const { isMangaV3 } = useExperience();
    const v3InputStyle: React.CSSProperties | undefined = isMangaV3 ? mv3.input : undefined;
    const inputClasses = `
      w-full px-4 py-2.5 bg-white dark:bg-gray-800 border rounded-xl
      ${error ? 'border-error-500 focus:ring-error-500 focus:border-error-500' : 'border-gray-300 dark:border-gray-600 focus:ring-secondary-500 focus:border-secondary-500'}
      ${leftIcon ? 'pl-10' : ''}
      ${rightIcon ? 'pr-10' : ''}
      ${className}
      dark:text-white placeholder-gray-400 dark:placeholder-gray-500
      disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500
      transition-all duration-200 shadow-inner-warm
    `;

    return (
      <div className="w-full">
        {label && (
          <label
            className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5"
            style={isMangaV3 ? mv3.heading : undefined}
          >
            {label}
          </label>
        )}
        {helpText && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{helpText}</p>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 dark:text-gray-500 sm:text-sm">{leftIcon}</span>
            </div>
          )}
          {multiline ? (
            <textarea
              className={inputClasses}
              rows={3}
              style={v3InputStyle}
              {...(props as any)}
            />
          ) : (
            <input
              ref={ref}
              className={inputClasses}
              style={v3InputStyle}
              {...props}
            />
          )}
          {rightIcon && (
            <div
              className={`absolute inset-y-0 right-0 pr-3 flex items-center ${onRightIconClick ? 'cursor-pointer' : 'pointer-events-none'}`}
              onClick={onRightIconClick}
            >
              <span className="text-gray-400 dark:text-gray-500 sm:text-sm hover:text-gray-600 dark:hover:text-gray-300">{rightIcon}</span>
            </div>
          )}
        </div>
        {error && <p className="mt-1.5 text-sm text-error-500">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
